import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Hero from '@/pages/home/components/Hero';
import CategorySection from '@/pages/home/components/CategorySection';
import { Link } from 'react-router-dom';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://kakobuyfinds.surf';

const homeSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebSite',
      name: 'KakobuyFinds',
      url: siteUrl,
      description: 'Niezależna platforma odkrywania produktów Kakobuy. Największa baza spreadsheetów w Polsce.',
      inLanguage: 'pl-PL',
      potentialAction: {
        '@type': 'SearchAction',
        target: `${siteUrl}/search?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@type': 'Organization',
      name: 'KakobuyFinds',
      url: siteUrl,
      description: 'Niezależna platforma badawcza i porównawcza pomagająca w odkrywaniu produktów, porównywaniu cen i znajdowaniu najlepszych okazji zakupowych.',
      foundingDate: '2024',
      areaServed: {
        '@type': 'Country',
        name: 'Polska',
      },
    },
  ],
};

const showcaseProducts = [
  {
    name: 'Luksusowe Okulary',
    image: 'https://readdy.ai/api/search-image?query=Premium%20sunglasses%20with%20tortoise%20shell%20frame%20elegantly%20placed%20on%20cream%20marble%20surface%2C%20soft%20golden%20hour%20light%20creating%20gentle%20shadows%2C%20luxury%20product%20photography%2C%20sophisticated%20editorial%20style%2C%20warm%20amber%20highlights%20reflecting%20on%20lens%2C%20minimalist%20composition%2C%20high-end%20boutique%20display%2C%20refined%20aesthetic%20with%20subtle%20warmth&width=600&height=600&seq=kakobuy-product-fresh-1&orientation=squarish',
  },
  {
    name: 'Zegarki Premium',
    image: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20wristwatch%20with%20leather%20strap%20resting%20on%20soft%20linen%20fabric%2C%20warm%20natural%20window%20light%2C%20delicate%20shadows%2C%20luxury%20product%20still%20life%2C%20sophisticated%20editorial%20photography%2C%20cream%20and%20taupe%20neutral%20palette%2C%20refined%20timepiece%20display%2C%20subtle%20texture%20details%2C%20high-end%20catalog%20quality&width=600&height=600&seq=kakobuy-product-fresh-2&orientation=squarish',
  },
  {
    name: 'Skórzane Torby',
    image: 'https://readdy.ai/api/search-image?query=Structured%20leather%20handbag%20placed%20on%20light%20oak%20wooden%20surface%2C%20soft%20diffused%20lighting%20from%20side%20window%2C%20gentle%20shadows%20revealing%20texture%2C%20premium%20product%20photography%2C%20editorial%20fashion%20still%20life%2C%20warm%20beige%20and%20caramel%20tones%2C%20minimalist%20luxury%20display%2C%20refined%20craftsmanship%20visible%2C%20boutique%20catalog%20aesthetic&width=600&height=600&seq=kakobuy-product-fresh-3&orientation=squarish',
  },
  {
    name: 'Designerskie Paski',
    image: 'https://readdy.ai/api/search-image?query=Elegant%20leather%20belt%20coiled%20gracefully%20on%20smooth%20cream%20surface%2C%20soft%20natural%20lighting%20highlighting%20buckle%20details%20and%20leather%20grain%2C%20sophisticated%20product%20still%20life%2C%20editorial%20accessories%20photography%2C%20warm%20neutral%20tones%20with%20subtle%20golden%20accents%2C%20minimalist%20luxury%20composition%2C%20refined%20craftsmanship%20showcase&width=600&height=600&seq=kakobuy-product-fresh-4&orientation=squarish',
  },
];

const featuredArticles = [
  {
    title: 'Kakobuy Sheet – Kompletny Przewodnik',
    excerpt: 'Dowiedz się wszystkiego o Kakobuy Spreadsheet: czym jest, jak działa, jakie produkty znajdziesz i dlaczego warto z niego korzystać. Kompletny przewodnik dla początkujących i zaawansowanych.',
    link: '/artykul/kakobuy-sheet-kompletny-przewodnik',
    tag: 'Poradnik',
    icon: 'ri-book-open-line',
  },
  {
    title: 'CSSBuy vs Kakobuy – Porównanie Platform',
    excerpt: 'Szczegółowe porównanie CSSBuy Spreadsheet i Kakobuy Spreadsheet. Która platforma jest lepsza? Analizujemy funkcje, ceny, dostępność produktów i jakość arkuszy.',
    link: '/artykul/cssbuy-vs-kakobuy-porownanie-spreadsheet',
    tag: 'Porównanie',
    icon: 'ri-scales-line',
  },
  {
    title: 'Bezpieczne Zakupy z Kakobuy',
    excerpt: 'Kompletny poradnik bezpieczeństwa. Dowiedz się, jak unikać oszustw, chronić swoje dane i bezpiecznie korzystać ze spreadsheetów zakupowych.',
    link: '/artykul/kakobuy-bezpieczne-zakupy-poradnik',
    tag: 'Bezpieczeństwo',
    icon: 'ri-shield-check-line',
  },
];

export default function Home() {
  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(homeSchema) }} />
      <Navbar />
      <main>
        <Hero />
        <CategorySection />

        <section id="about" className="py-16 md:py-24 bg-background-100">
          <div className="w-full max-w-[1400px] mx-auto px-6 md:px-10">
            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
              <div className="lg:w-5/12 relative">
                <span className="inline-block px-3 py-1 text-xs font-medium rounded-full border border-secondary-300 text-secondary-600 mb-4">
                  Spreadsheet Hub
                </span>
                <div className="rounded-3xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Clean%20bright%20workspace%20with%20laptop%20displaying%20organized%20data%20tables%2C%20ceramic%20coffee%20cup%2C%20small%20potted%20succulent%20plant%2C%20and%20linen%20notebook%20on%20warm%20oak%20desk%20surface%2C%20soft%20natural%20daylight%20streaming%20through%20sheer%20curtains%2C%20calming%20productive%20atmosphere%2C%20scandinavian%20modern%20interior%2C%20cream%20and%20wood%20tones%2C%20editorial%20home%20office%20photography%2C%20peaceful%20organized%20aesthetic&width=800&height=600&seq=kakobuy-about-fresh-2026&orientation=landscape"
                    alt="Kakobuy Spreadsheet workspace"
                    title="Kakobuy Spreadsheet - Twoja niezależna baza produktów"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="lg:w-7/12">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-foreground-950 leading-tight">
                  Twoja Niezależna
                  <br />
                  Baza Produktów
                  <br />
                  <span className="text-primary-500">Kakobuy</span>
                </h2>
                <p className="mt-6 text-base md:text-lg text-foreground-600 leading-relaxed max-w-[540px]">
                  KakobuyFinds to niezależna platforma badawcza i porównawcza, która pomaga użytkownikom odkrywać najlepsze produkty, porównywać ceny i znajdować najciekawsze okazje zakupowe. Nie jesteśmy oficjalną stroną Kakobuy — jesteśmy Twoim przewodnikiem po świecie spreadsheetów produktowych.
                </p>
                <p className="mt-4 text-base text-foreground-600 leading-relaxed max-w-[540px]">
                  Nasze arkusze są regularnie aktualizowane, zawierają szczegółowe informacje o produktach i pomagają zaoszczędzić godziny na ręcznym researchu. Dołącz do społeczności tysięcy zadowolonych użytkowników.
                </p>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 mt-8 px-6 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  Odkryj Kaspreadsheet
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-foreground-950">
          <div className="w-full max-w-[1400px] mx-auto px-6 md:px-10">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-10 gap-6">
              <span className="inline-block self-start px-4 py-1.5 text-xs font-medium tracking-widest uppercase rounded-full border border-white/20 text-white/70">
                Trending Now
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-white leading-tight lg:max-w-[600px]">
                Najnowsze Produkty z Kakobuy
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
              {showcaseProducts.map((product) => (
                <a
                  key={product.name}
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="group cursor-pointer"
                >
                  <div className="aspect-square rounded-2xl bg-background-100 overflow-hidden mb-3">
                    <img
                      src={product.image}
                      alt={product.name}
                      title={`Kakobuy Spreadsheet - ${product.name}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <p className="text-sm font-medium text-white/90 group-hover:text-white transition-colors">
                    {product.name}
                  </p>
                  <p className="text-xs text-white/50 mt-1">Zaktualizowano: dziś</p>
                </a>
              ))}
            </div>
          </div>
        </section>

        <section id="cta" className="py-16 md:py-24 bg-background-50">
          <div className="w-full max-w-[800px] mx-auto px-6 md:px-10 text-center">
            <h2 className="text-3xl md:text-5xl font-bold font-heading text-foreground-950">
              Gotowy na Zakupy?
            </h2>
            <p className="mt-4 text-base md:text-lg text-foreground-600 max-w-[550px] mx-auto leading-relaxed">
              Przejdź do pełnej bazy produktów i odkryj tysiące okazji na Kaspreadsheet. Wszystkie produkty w jednym miejscu, regularnie aktualizowane.
            </p>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 mt-8 px-8 py-4 text-base font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              Zobacz Pełną Bazę na Kaspreadsheet
              <i className="ri-arrow-right-line"></i>
            </a>
            <div className="mt-14 flex flex-nowrap justify-center gap-3 overflow-x-auto px-2">
              {['👟', '🕶️', '⌚', '👜', '💼', '🧥', '🧢', '💎'].map((emoji, i) => (
                <span key={i} className="text-3xl md:text-4xl flex-shrink-0 opacity-70 hover:opacity-100 transition-opacity cursor-default">
                  {emoji}
                </span>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-background-100">
          <div className="w-full max-w-[800px] mx-auto px-6 md:px-10">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-800 text-center italic">
              (Co Mówią Użytkownicy)
            </h2>
            <div className="mt-10 flex flex-col items-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-foreground-950 rounded-full">
                <i className="ri-star-fill text-accent-400 text-sm"></i>
                <span className="text-white text-sm font-semibold">4.9/5</span>
              </div>
              <blockquote className="mt-8 text-lg md:text-xl text-foreground-700 text-center leading-relaxed max-w-[600px] font-heading italic">
                &ldquo;Dzięki KakobuyFinds zaoszczędziłem dziesiątki godzin na szukaniu produktów. Spreadsheety są zawsze aktualne, a kategorie świetnie zorganizowane. To moje główne narzędzie do researchu przed zakupami.&rdquo;
              </blockquote>
              <div className="mt-8 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-200 flex items-center justify-center text-primary-700 font-bold text-sm">
                  MK
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground-900">Michał K.</p>
                  <p className="text-xs text-foreground-500">Stały użytkownik od 2024</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-background-50">
          <div className="w-full max-w-[1400px] mx-auto px-6 md:px-10">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 text-xs font-medium tracking-widest uppercase rounded-full border border-accent-300 text-accent-600 mb-4">
                Poradniki
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-foreground-950">
                Nasze Artykuły i Poradniki
              </h2>
              <p className="mt-4 text-base text-foreground-600 max-w-[550px] mx-auto">
                Poznaj świat spreadsheetów zakupowych dzięki naszym szczegółowym przewodnikom
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featuredArticles.map((article) => (
                <div key={article.link} className="bg-background-100 rounded-3xl p-6 md:p-7 flex flex-col">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
                      <i className={`${article.icon} text-accent-600`}></i>
                    </span>
                    <span className="text-xs font-medium text-accent-700">{article.tag}</span>
                  </div>
                  <h3 className="text-lg font-bold font-heading text-foreground-900 mb-3 leading-snug">
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed flex-1 mb-5">
                    {article.excerpt}
                  </p>
                  <Link
                    to={article.link}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-primary-500 hover:text-primary-600 transition-colors mt-auto whitespace-nowrap cursor-pointer"
                  >
                    Czytaj Artykuł
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}